import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { AuditEntryDto } from '@markant-platform/shared/audit/data-access';
import { AuditChangeItem, ChangeSetTransformer } from './model/audit.config';
import { formatToTitleCase, MarkantSelectOption } from '@markant-platform/shared/util-helpers';
import { Filter } from '@markant-platform/shared/ui-filter';
import { OrgChangeSetFields, OrgTransformerOptions } from './org-changeset.options';

@Injectable({
  providedIn: 'root',
})
export class OrgChangeSetTransformer implements ChangeSetTransformer {
  private translocoService: TranslocoService = inject(TranslocoService);

  public transform = (items: AuditEntryDto[]): AuditChangeItem[] => {
    return items.flatMap(({ changeSet, modifiedBy, modifiedAt, reason }) => {
      if (!changeSet) {
        return [];
      }

      return Object.keys(changeSet).map(field => {
        let { previousValue, currentValue } = changeSet[field];

        if ('platformRequestStatus' in changeSet) {
          previousValue = previousValue && formatToTitleCase(previousValue);
          currentValue = currentValue && formatToTitleCase(currentValue);
        }

        return {
          key: field,
          label: this.translateField(field),
          previousValue: this.translate(field, previousValue),
          currentValue: this.translate(field, currentValue),
          modifiedBy,
          modifiedAt,
          reason,
        };
      });
    });
  };

  public readonly provideFilters = (): Filter<MarkantSelectOption> => {
    const filterOptions = Object.entries(OrgTransformerOptions.displayFields).map(
      ([key, value]) =>
        ({
          value: key,
          representation: this.translocoService.translate(value),
        }) as MarkantSelectOption
    );
    return {
      field: {
        groupName: 'field',
        translationKey: 'audit.table.filter.attribute',
        filterOptions,
      },
    };
  };

  private translateField = (field: string): string => {
    return this.translocoService.translate(OrgTransformerOptions.displayFields[field as OrgChangeSetFields]);
  };

  private translate = (field: string, value?: string): string | undefined => {
    if (!value) {
      return;
    }

    const isoTranslation = OrgTransformerOptions.isoValues[field as OrgChangeSetFields] as Intl.DisplayNamesType;

    if (isoTranslation) {
      const lang = this.translocoService.getActiveLang();
      const intl = new Intl.DisplayNames(lang, { type: isoTranslation });
      return intl.of(value);
    }

    return value;
  };
}
